/**
 * 运维 —— 版本矩阵 / 组件健康 / OTA / 配置下发 / 远程日志。
 *
 * 这一页的成色分成两半, 而且分界线**不是**"做了没做":
 *
 *   已经有源(现在就真):
 *     · 版本矩阵 —— 遥测里带着每台设备的 build sha 与上报时刻。这一页原来写着
 *       "hub 不知道任何设备的版本", 那句话在遥测接通之后就过期了。
 *     · 组件健康 —— 设备把自己那 18 个组件的启停与传感器速率随遥测报上来。
 *       ★ 判定在设备侧, 云端**不重判**:一个组件算不算活着, 要看 ROS 节点注册、
 *       发布速率这些只有本机看得到的东西(§9b:仪器要在被测对象那一侧)。
 *
 *   仍然没有(而且其中一条是【故意不做】):
 *     · OTA / 远程日志 —— 制品仓库、下行通道、日志汇聚, 一样都还没建。
 *     · 配置下发 —— 这条不是"还没做"。仓库是配置的唯一事实源(CLAUDE.md §0.1),
 *       一个云端配置编辑器恰恰会造出那种【绕开仓库、对账工具全瞎】的旁路 ——
 *       2026-08-06 就是这么被一行留在 auth.env 里的临时改动坑了一整天。
 *       所以这一格不该被"补上", 它该一直空着, 并说明为什么。
 */
import { useTerminals } from '@/hooks/useHubData';
import { LoadError } from '@/components/feature/DataSource';

/** 上报多久之内算"这份健康还新鲜"。遥测是每分钟一次。 */
const FRESH_S = 180;

export default function OpsPage() {
  const { terminals, loading, error, refetch } = useTerminals();

  const reported = terminals.filter((t) => t.services || t.version);

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="animate-fade-in-up">
        <h1 className="text-lg font-bold text-foreground-900 font-heading">运维管理</h1>
        <p className="text-xs text-foreground-500 mt-0.5">
          版本矩阵与组件健康来自设备遥测(每分钟一次, 判定在设备侧);OTA / 配置下发 / 远程日志见下方说明
        </p>
      </div>

      {error && <LoadError error={error} onRetry={refetch} />}
      {loading && !terminals.length && <p className="text-xs text-foreground-400">读取设备…</p>}

      {/* ── 版本矩阵 ── */}
      {terminals.length > 0 && (
        <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 animate-fade-in-up">
          <h2 className="text-xs font-bold text-foreground-800 mb-0.5">版本矩阵</h2>
          <p className="text-[10px] text-foreground-400 mb-3">
            build sha 由设备随遥测上报(部署流程写的 VERSION 戳)。没报过的显示"待上报", 不显示"未知版本"。
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-foreground-400 text-left">
                  <th className="pb-1.5 font-medium">设备</th>
                  <th className="pb-1.5 font-medium">类型</th>
                  <th className="pb-1.5 font-medium">build</th>
                  <th className="pb-1.5 font-medium">遥测</th>
                  <th className="pb-1.5 font-medium">在线</th>
                </tr>
              </thead>
              <tbody>
                {terminals.map((t) => (
                  <tr key={t.id} className="border-t border-background-200/50">
                    <td className="py-1.5 text-foreground-800">{t.name}</td>
                    <td className="py-1.5 text-foreground-500">{t.type}</td>
                    <td className="py-1.5 font-mono text-foreground-700">{t.version ?? <span className="text-foreground-400">待上报</span>}</td>
                    <td className="py-1.5 font-mono text-foreground-500">{t.telemetryAt ? ageOf(t.telemetryAt) : '—'}</td>
                    <td className="py-1.5">
                      <span className={`inline-flex items-center gap-1 ${t.status === 'online' ? 'text-accent-600' : 'text-foreground-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${t.status === 'online' ? 'bg-accent-500' : 'bg-foreground-300'}`} />
                        {t.status === 'online' ? '在线' : '离线'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── 组件健康 ── */}
      <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 animate-fade-in-up" style={{ animationDelay: '0.06s' }}>
        <h2 className="text-xs font-bold text-foreground-800 mb-0.5">组件健康</h2>
        <p className="text-[10px] text-foreground-400 mb-3">
          设备自报的组件启停与传感器速率。★ 云端不重判:"某个组件算不算活着"要看 ROS 节点注册与发布速率,
          只有设备本机看得到 —— 在这里另判一次只会得到一个更差的第二意见。
        </p>
        {reported.filter((t) => t.services).length === 0 ? (
          <p className="text-[11px] text-foreground-400 py-4 text-center">
            还没有设备上报过组件健康(需要设备侧遥测脚本带上 extra.services)。
          </p>
        ) : (
          <div className="space-y-4">
            {reported.filter((t) => t.services).map((t) => {
              const svc = t.services as Record<string, boolean>;
              const names = Object.keys(svc).sort();
              const down = names.filter((n) => !svc[n]);
              const stale = !t.telemetryAt || (Date.now() - new Date(t.telemetryAt).getTime()) / 1000 > FRESH_S;
              return (
                <div key={t.id}>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <span className="text-[11px] font-medium text-foreground-800">{t.name}</span>
                    <span className="text-[10px] text-foreground-400">
                      {names.length - down.length}/{names.length} 在跑
                      {/* 陈旧的健康报告要说自己陈旧 —— 一份三小时前的"全绿"不是全绿。 */}
                      {stale && <span className="text-amber-600 ml-1.5">· 这份健康是 {t.telemetryAt ? ageOf(t.telemetryAt) : '很久'}前的</span>}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {names.map((n) => (
                      <span key={n}
                        className={`px-2 py-0.5 rounded text-[10px] font-mono ${
                          svc[n] ? 'bg-accent-500/10 text-accent-700' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                        {svc[n] ? '● ' : '✕ '}{n}
                      </span>
                    ))}
                  </div>
                  {t.sensorHz && (
                    <div className="flex flex-wrap gap-3 mt-2 text-[10px] font-mono text-foreground-500">
                      {Object.entries(t.sensorHz).map(([k, v]) => (
                        <span key={k} className={v === 0 ? 'text-red-600' : ''}>{k} {v} Hz</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── 仍然没有的三样 ── */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
        <Missing icon="ri-upload-cloud-line" title="OTA 升级"
          why="需要制品仓库 + 分阶段下发 + 失败回滚。三样都还没有。现在的发布路径是开发机构建 → deploy_bin.sh/deploy_so.sh 直发, 有 sha 对账与 .prev 回滚。" />
        <Missing icon="ri-terminal-box-line" title="远程日志 / 诊断"
          why="日志留在设备本地(~/0xman/logs)。要做需要上报链路与保留策略;在那之前, 现场排障走 SSH + oxdiag.sh, 而不是在这里看一段可能不全的摘要。" />
        <Missing icon="ri-settings-3-line" title="配置下发" deliberate
          why="★ 这一格【故意留空】。仓库是配置的唯一事实源(§0.1):云端改配置会造出一条绕开仓库、所有对账工具都看不见的旁路 —— 2026-08-06 一行留在 auth.env 的临时改动因此活了一整天, 而现场表现是'走过的地方没有栅格'。要加旋钮, 就让它在仓库里是一个带默认值的参数, 走发布流程。" />
      </section>
    </div>
  );
}

function Missing({ icon, title, why, deliberate }: { icon: string; title: string; why: string; deliberate?: boolean }) {
  return (
    <div className={`rounded-lg p-4 border ${deliberate ? 'bg-amber-50/60 border-amber-200/70' : 'bg-background-50 border-background-200/70'}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500"><i className={`${icon} text-base`} /></span>
        <h3 className="text-xs font-bold text-foreground-800">{title}</h3>
        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${deliberate ? 'bg-amber-100 text-amber-700' : 'bg-background-100 text-foreground-400'}`}>
          {deliberate ? '故意不做' : '没有数据源'}
        </span>
      </div>
      <p className="text-[10px] text-foreground-500 leading-relaxed">{why}</p>
    </div>
  );
}

/** "多久以前" —— 和位姿/地图同一套措辞, 粗到能读, 不给假精度。 */
function ageOf(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return `${Math.round(s)} 秒`;
  if (s < 5400) return `${Math.round(s / 60)} 分钟`;
  return `${(s / 3600).toFixed(1)} 小时`;
}
